<template>
  <BContainer
    v-if="currentUser"
    class="mb-5 px-2"
  >
    <div
      class="px-0 py-3"
    >
      <BRow
        v-if="fetching"
        class="gap-y-5 mt-5 d-flex justify-content-center align-items-center"
      >
        <BCol
          cols="12"
        >
          <div class="text-center py-5">
            <BIcon
              icon="stopwatch"
              font-scale="5"
              animation="cylon"
            />
            <p class="h3 py-2">
              <strong>Loading . . .</strong>
            </p>
          </div>
        </BCol>
      </BRow>

      <BRow
        v-if="!fetching && memos.length === 0"
        class="gap-y-5 mt-5 d-flex justify-content-center align-items-center"
      >
        <BCol
          cols="12"
          class="text-center py-5"
        >
          <BIcon
            icon="file-pdf"
            font-scale="5"
          />
          <p class="h3 py-2">
            <strong>No memos found</strong>
          </p>
        </BCol>
      </BRow>

      <BRow
        v-else
        class="gap-y-5"
      >
        <BCol
          v-for="(memo, index) in memos"
          :key="index"
          cols="12"
          md="6"
        >
          <div class="card px-3 pt-3 pb-2 rounded-lg">
            <div class="d-flex gap-2 align-items-center p-1 rounded-lg">
              <div class="d-flex flex-column gap-2 flex-grow-1">
                <div>
                  <div class="border-bottom pb-1">
                    <div
                      style="font-size: 10px;"
                      class="text-muted"
                    >
                      CUSTOMER MEMO
                    </div>
                    <div
                      class="text-primary"
                      style="font-size: 14px; font-weight: 700; margin-top: 2px;"
                    >
                      {{ memo.memo_name }}
                    </div>
                  </div>
                  <div
                    class="mt-3 d-flex flex-column gap-3"
                  >
                    <BRow>
                      <BCol
                        cols="7"
                        md="6"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div style="width: 25px;">
                            <i class="fa-xl fad fa-user" />
                          </div>
                          <div class="flex-grow-1">
                            <div
                              style="font-size: 10px;"
                              class="text-muted"
                            >
                              PUBLISHER
                            </div>
                            <div style="font-size: 10px; font-weight: 700; margin-top: 2px;">
                              {{ memo.creator?.first_name || '' }} {{ memo.creator?.last_name || '' }}
                            </div>
                          </div>
                        </div>
                      </BCol>
                      <BCol
                        cols="5"
                        md="6"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div style="width: 25px;">
                            <i class="fa-xl fad fa-paperclip" />
                          </div>
                          <div>
                            <div
                              style="font-size: 10px;"
                              class="text-muted"
                            >
                              ATTACHMENTS
                            </div>
                            <div style="font-size: 10px; font-weight: 700; margin-top: 2px;">
                              {{ memo.attachments?.length || 0 }} file{{ memo.attachments?.length > 1 ? 's' : '' }}
                            </div>
                          </div>
                        </div>
                      </BCol>
                    </BRow>
                    <BRow class="gap-y-3">
                      <BCol
                        cols="12"
                        md="6"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div style="width: 25px;">
                            <i class="fa-xl fad fa-calendar-clock" />
                          </div>
                          <div>
                            <div
                              style="font-size: 10px;"
                              class="text-muted"
                            >
                              PUBLISHED AT
                            </div>
                            <div
                              class="d-flex flex-row"
                              style="font-size: 10px; font-weight: 700; margin-top: 2px;"
                            >
                              <div>{{ dateShortFormatter(memo?.created_at || '') }}</div>
                              <div>&nbsp;@ {{ timeFormatter(memo?.created_at || '') }}</div>
                            </div>
                          </div>
                        </div>
                      </BCol>
                      <BCol
                        cols="12"
                        md="6"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div style="width: 25px;">
                            <i class="fa-xl fad fa-clock-rotate-left" />
                          </div>
                          <div>
                            <div
                              style="font-size: 10px;"
                              class="text-muted"
                            >
                              UPDATED AT
                            </div>
                            <div
                              class="d-flex flex-row"
                              style="font-size: 10px; font-weight: 700; margin-top: 2px;"
                            >
                              <div>{{ dateShortFormatter(memo?.updated_at || '') }}</div>
                              <div>&nbsp;@ {{ timeFormatter(memo?.updated_at || '') }}</div>
                            </div>
                          </div>
                        </div>
                      </BCol>
                    </BRow>
                  </div>
                </div>
                <div class="d-flex justify-content-end">
                  <BButton
                    variant="warning"
                    size="sm"
                    @click.prevent="() => viewMemo(memo)"
                  >
                    View
                  </BButton>
                </div>
              </div>
            </div>
          </div>
        </BCol>
      </BRow>
    </div>

    <BModal
      ref="modalMemo"
      size="lg"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      title="Customer Memo"
      @shown="() => attachArrowScroller('.nav-memos-tabs')"
      @hidden="() => detachArrowScroller('.nav-memos-tabs')"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-memos-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-file-lines" />
              <b class="is-tab-active ml-1">
                Details
              </b>
            </div>
          </template>

          <BRow>
            <BCol
              cols="12"
            >
              <ValidationObserver
                ref="formMemo"
              >
                <form
                  role="form"
                  novalidate
                  @submit.prevent
                >
                  <BRow>

                    <BCol
                      cols="12"
                    >
                      <BFormGroup
                        label="Memo Code"
                        label-for="fMemoCode"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fMemoCode"
                          v-model="formMemo.memo_code"
                          type="text"
                          maxlength="150"
                          autocomplete="off"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                    >
                      <BFormGroup
                        label="Memo Name"
                        label-for="fMemoName"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fMemoName"
                          v-model="formMemo.memo_name"
                          type="text"
                          maxlength="150"
                          autocomplete="off"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup
                        label="Start Date"
                        label-for="fStartDate"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fStartDate"
                          v-model="formMemo.start_date"
                          type="date"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                      md="6"
                    >
                      <BFormGroup
                        label="End Date"
                        label-for="fEndDate"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <BFormInput
                          id="fEndDate"
                          v-model="formMemo.end_date"
                          type="date"
                          disabled
                        />
                      </BFormGroup>
                    </BCol>

                    <BCol
                      cols="12"
                    >
                      <BTable
                        :items="tableDocuments.rows"
                        :fields="tableDocuments.fields"
                        :sort-by="tableDocuments.sortBy"
                        :sort-desc="tableDocuments.sortDesc"
                        :sort-direction="tableDocuments.sortDirection"
                        :stacked="tableDocuments.stacked"
                        caption-top
                      >
                        <template #cell(index)="data">
                          {{ data.index + 1 }}
                        </template>

                        <template #table-caption>
                          <b>Required Documents</b>
                        </template>
                      </BTable>
                    </BCol>
                  </BRow>
                </form>
              </ValidationObserver>

            </BCol>
          </BRow>
        </BTab>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-paperclip" />
              <b class="is-tab-active ml-1">
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :path="formMemo.attachment_path"
            :files="formMemo.attachments"
            :show-uploader="false"
          />
        </BTab>
      </BTabs>

      <template #modal-footer="{cancel}">
        <BButton
          variant="outline-dark"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import { mapGetters } from 'vuex'

import Attachment from '@/views/asm/customer-memos/components/memos/Attachment.vue'

import MxImageUrl from '@/mixins/image-url'
import MxFormatter from '@/mixins/formatter'
import MxMisc from '@/mixins/misc'

import { SSharedList } from '@/services/shared'

export default {
  name: 'ASMMemosDocuments',

  components: {
    Attachment
  },

  mixins: [
    MxMisc,
    MxImageUrl,
    MxFormatter
  ],

  middleware: ['auth'],

  data () {
    return {
      fetching: false,
      formMemo: {
        id: '',
        team_id: '',
        memo_code: '',
        memo_name: '',
        start_date: '',
        end_date: '',
        attachment_path: '',
        attachments: [],
        active: 1
      },
      memos: [],
      tableDocuments: {
        rows: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        stacked: 'sm',
        fields: [
          { mobile: 0, key: 'index', label: '#' },
          { mobile: 2, key: 'document_type_name', label: 'Document Type' },
          { mobile: 3, key: 'upload_limit', label: 'Upload Limit' },
          { mobile: 4, key: 'required', label: 'Is Required?', formatter: this.yesOrNo }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    pdfImageSrc () {
      return `${window.location.origin}/img/pdf.png`
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getMemos()
    }
  },

  methods: {
    getFormSrc (formName) {
      return `${window.location.origin}/forms/${formName}`
    },

    viewMemo (memo) {
      this.formMemo = Object.assign(this.formMemo, {
        id: memo.id,
        team_id: memo.team_id,
        memo_code: memo.memo_code,
        memo_name: memo.memo_name,
        start_date: memo.start_date,
        end_date: memo.end_date,
        attachment_path: memo.attachment_path,
        attachments: memo.attachments || [],
        active: 1
      })

      this.tableDocuments.rows = memo.documents || []

      this.$refs.modalMemo.show()
    },

    async getMemos () {
      this.fetching = true

      return SSharedList.getMemos().then(({ data }) => {
        this.memos = data.map(
          memo => Object.assign(memo, {
            src: 'consent-form.pdf'
          })
        )
      }).catch(() => {
        this.memos = []
      }).finally(() => {
        this.fetching = false
      })
    }
  }
}
</script>
<style scoped>
.form-content {
  max-height: 220px;
  min-height: 220px;
}

@media screen and (max-width: 985px) {
  .form-content {
    max-height: 150px;
    min-height: 150px;
  }
}

@media screen and (max-width: 516px) {
  .form-content {
    max-height: 180px;
    min-height: 180px;
  }
}

</style>
